import type {CitationTarget,FileRecord} from '@reados/contracts';

export const TEXT_PREVIEW_LIMIT=2*1024*1024;
export type MarkdownMode='rendered'|'source';
export interface SourceLineRange {start:number;end:number}
export interface TextPreview {text:string;truncated:boolean}

export function isMarkdownDocument(file:Pick<FileRecord,'name'|'mimeType'>):boolean {
 const mime=file.mimeType.split(';',1)[0].trim().toLowerCase();
 return /\.(md|markdown|mdown|mkd)$/i.test(file.name)||mime==='text/markdown'||mime==='text/x-markdown';
}
export function documentStateKey(file:Pick<FileRecord,'id'|'contentRevision'>):string {
 return `reados.document.${encodeURIComponent(file.id)}.${encodeURIComponent(file.contentRevision)}.dil`;
}
export function documentModeKey(file:Pick<FileRecord,'id'>):string {return `reados.document.${encodeURIComponent(file.id)}.mode`;}
export function sourceLineRange(target?:Pick<CitationTarget,'lineStart'|'lineEnd'>):SourceLineRange|undefined {
 if(!Number.isSafeInteger(target?.lineStart)||target!.lineStart!<1)return undefined;
 const start=target!.lineStart!,end=target?.lineEnd;
 return {start,end:Number.isSafeInteger(end)&&end!>=start?end!:start};
}
export function splitSourceLines(text:string,range?:SourceLineRange):{before:string;selected:string;after:string;range?:SourceLineRange} {
 if(!range)return {before:text,selected:'',after:''};
 const lines=text.split('\n');
 if(range.start>lines.length)return {before:text,selected:'',after:''};
 const end=Math.min(range.end,lines.length);
 const before=lines.slice(0,range.start-1).join('\n')+(range.start>1?'\n':'');
 const selected=lines.slice(range.start-1,end).join('\n')+(end<lines.length?'\n':'');
 return {before,selected,after:lines.slice(end).join('\n'),range:{start:range.start,end}};
}

/** Read one bounded, complete prefix. Incomplete HTTP ranges must never become a DIL program. */
export async function readTextPreview(response:Response,limit=TEXT_PREVIEW_LIMIT,expectedRevision?:string):Promise<TextPreview> {
 if(response.status===416&&response.headers.get('content-range')==='bytes */0')return decodeTextPreview(new Uint8Array(),false,expectedRevision);
 if(!response.ok)throw new Error(`读取失败（${response.status}）`);
 let resourceSize:number|undefined,rangeLength:number|undefined;
 if(response.status===206){
  const range=/^bytes 0-(\d+)\/(\d+)$/.exec(response.headers.get('content-range')||'');
  if(!range)throw new Error('文件返回的文本范围无效，请重试。');
  const end=Number(range[1]);resourceSize=Number(range[2]);
  if(!Number.isSafeInteger(end)||!Number.isSafeInteger(resourceSize)||end<0||end>=resourceSize)throw new Error('文件返回的文本范围无效，请重试。');
  rangeLength=end+1;
 }
 const reader=response.body?.getReader();
 if(!reader){
  if(response.status===204||response.headers.get('content-length')==='0')return decodeTextPreview(new Uint8Array(),false,expectedRevision);
  throw new Error('浏览器无法读取此文件');
 }
 const chunks:Uint8Array[]=[];let total=0;
 try {
  while(total<=limit){
   const result=await reader.read();if(result.done)break;
   const piece=result.value.subarray(0,limit+1-total);chunks.push(piece);total+=piece.length;
   if(total>limit){await reader.cancel();break;}
  }
 } finally {reader.releaseLock();}
 if(resourceSize!==undefined&&(total<Math.min(resourceSize,limit+1)||total!==Math.min(rangeLength!,limit+1)))throw new Error('文本预览不完整，请重试。');
 const truncated=total>limit||(resourceSize!==undefined&&resourceSize>limit);
 const bytes=new Uint8Array(Math.min(total,limit));let offset=0;
 for(const chunk of chunks){const piece=chunk.subarray(0,bytes.length-offset);bytes.set(piece,offset);offset+=piece.length;if(offset>=bytes.length)break;}
 return decodeTextPreview(bytes,truncated,expectedRevision);
}

async function decodeTextPreview(bytes:Uint8Array,truncated:boolean,expectedRevision?:string):Promise<TextPreview> {
 if(!truncated&&expectedRevision!==undefined){
  if(!/^[a-f0-9]{64}$/i.test(expectedRevision))throw new Error('资料版本缺少有效校验信息，请刷新资料库后重试。');
  if(!globalThis.crypto?.subtle)throw new Error('当前浏览器无法校验文件内容，请使用受信任的 HTTPS 地址打开 ReadOS。');
  // Bind the raw bytes before UTF-8 decoding (which can remove a BOM) to the
  // catalog revision that owns DIL state and server-verified reference paths.
  const digest=await crypto.subtle.digest('SHA-256',new Uint8Array(bytes));
  const revision=Array.from(new Uint8Array(digest),value=>value.toString(16).padStart(2,'0')).join('');
  if(revision!==expectedRevision.toLowerCase())throw new Error('文件内容与当前资料版本不一致，请刷新资料库后重试。');
 }
 // Stream decoding omits an incomplete final UTF-8 sequence from a truncated prefix.
 const text=new TextDecoder().decode(bytes,{stream:truncated});
 return {text,truncated};
}
